import { Box, Typography, CircularProgress } from '@mui/material';
import { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { OfferCluster, OfferSearchResult, SearchFilters } from '../types/search.types';
import { AppButton } from '@/shared/components';
import { MapMarkerPopup } from './MapMarkerPopup';
import { useSearchContext } from '@/shared/contexts/SearchContext';

/** Default center: France */
const DEFAULT_CENTER: atlas.data.Position = [2.2137, 46.2276];
const DEFAULT_ZOOM = 5;
const MAP_CONTAINER_ID = 'azure-map-container';

interface MapViewProps {
  readonly offers: OfferSearchResult[];
  readonly isLoading: boolean;
  readonly searchFilters?: SearchFilters;
  readonly onSwitchToList?: () => void;
}

/**
 * Formats distance to 1 decimal place, e.g. 2.456 → "2.5km"
 */
function formatDistance(distance?: number): string {
  if (distance === undefined || distance === null) return '–';
  return `${distance.toFixed(1)}km`;
}

/** Key used to group offers at the same coordinate (5 dp ≈ 1 m precision). */
function coordKey(lat: number, lng: number): string {
  return `${lat.toFixed(5)},${lng.toFixed(5)}`;
}

/** Group valid offers by coordinate, returning one cluster per unique position. */
function clusterOffers(offers: OfferSearchResult[]): OfferCluster[] {
  const map = new Map<string, OfferCluster>();
  for (const offer of offers) {
    if (offer.latitude == null || offer.longitude == null) continue;
    const key = coordKey(offer.latitude, offer.longitude);
    if (!map.has(key)) {
      map.set(key, {
        position: [offer.longitude, offer.latitude],
        offers: [],
        minDistance: undefined,
      });
    }
    const cluster = map.get(key)!;
    cluster.offers.push(offer);
    if (
      offer.distance !== undefined &&
      (cluster.minDistance === undefined || offer.distance < cluster.minDistance)
    ) {
      cluster.minDistance = offer.distance;
    }
  }
  return Array.from(map.values());
}

/**
 * Single-offer marker: distance badge + pin image.
 * When isActive, the badge turns orange (AC-019.13).
 */
function createMarkerHtml(distance?: number, isActive = false): string {
  const distText = formatDistance(distance);
  return `
    <div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;" data-testid="map-marker">
      <div data-marker-badge style="
        background: ${isActive ? '#E8680A' : '#1B3A5C'};
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 8px;
        margin-bottom: 2px;
        white-space: nowrap;
        line-height: 1.3;
      ">${distText}</div>
      <img src="${isActive ? '/icons/map-marker-selected.svg' : '/icons/map-marker.svg'}" width="36" height="46" alt="marker" style="display:block;" />
    </div>
  `;
}

/**
 * Cluster marker: stacked pins with a count bubble on top.
 * When isActive, the badge turns orange (AC-019.13).
 */
function createClusterMarkerHtml(count: number, minDistance?: number, isActive = false): string {
  const distText = formatDistance(minDistance);
  return `
    <div style="display:flex;flex-direction:column;align-items:center;cursor:pointer;" data-testid="map-marker-cluster">
      <div data-marker-badge style="
        background: ${isActive ? '#E8680A' : '#1B3A5C'};
        color: #fff;
        font-size: 11px;
        font-weight: 600;
        padding: 2px 6px;
        border-radius: 8px;
        margin-bottom: 2px;
        white-space: nowrap;
        line-height: 1.3;
      ">${distText}</div>
      <div style="position:relative;width:36px;height:46px;">
        <!-- shadow pin shifted right -->
        <img src="/icons/map-marker.svg" width="30" height="38" alt="" style="position:absolute;bottom:0;right:0;opacity:0.5;" />
        <!-- main pin -->
        <img src="${isActive ? '/icons/map-marker-selected.svg' : '/icons/map-marker.svg'}" width="36" height="46" alt="marker" style="position:absolute;bottom:0;left:0;" />
        <!-- count bubble -->
        <div style="
          position:absolute;
          top:-2px;
          right:-4px;
          background:#F0C53D;
          border:2px solid ${isActive ? '#E8680A' : '#1B3A5C'};
          color:#1B3A5C;
          font-size:10px;
          font-weight:700;
          width:18px;
          height:18px;
          border-radius:50%;
          display:flex;
          align-items:center;
          justify-content:center;
          line-height:1;
        ">${count}</div>
      </div>
    </div>
  `;
}

export function MapView({
  offers,
  isLoading,
  searchFilters,
  onSwitchToList,
}: Readonly<MapViewProps>) {
  const { t } = useTranslation();
  const { searchContext, setMapViewport } = useSearchContext();

  // Map refs
  const outerBoxRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<atlas.Map | null>(null);
  const markersRef = useRef<atlas.HtmlMarker[]>([]);
  const clustersRef = useRef<OfferCluster[]>([]);
  /**
   * Flag set by a marker's DOM click handler so the map-level 'click' handler
   * knows not to close the popup (marker clicks bubble up to the map canvas).
   */
  const markerClickedRef = useRef(false);
  /**
   * Set to true when the map's ready callback restores a saved viewport.
   * The markers effect checks this to skip the default zoom-12 auto-center,
   * preserving the user's previous zoom after back-navigation.
   */
  const viewportRestoredRef = useRef(false);

  // UI state
  const [mapError, setMapError] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [selectedClusterIndex, setSelectedClusterIndex] = useState<number | null>(null);
  const [anchorPx, setAnchorPx] = useState<{ x: number; y: number } | null>(null);

  const cleanupMarkers = useCallback(() => {
    if (mapRef.current) {
      markersRef.current.forEach(marker => {
        mapRef.current?.markers.remove(marker);
      });
    }
    markersRef.current = [];
  }, []);

  /**
   * Update active marker appearance via direct DOM mutation (NOT setOptions).
   * Using setOptions({ htmlContent }) would replace the marker's DOM element,
   * destroying the attached click listener. Direct mutation preserves listeners.
   */
  const refreshMarkerHtml = useCallback((activeIdx: number | null) => {
    clustersRef.current.forEach((_cluster, idx) => {
      const isActive = idx === activeIdx;
      const el = markersRef.current[idx]?.getElement();
      if (!el) return;
      const badge = el.querySelector<HTMLElement>('[data-marker-badge]');
      if (badge) {
        badge.style.background = isActive ? '#E8680A' : '#1B3A5C';
      }
      const pin = el.querySelector<HTMLImageElement>('img[alt="marker"]');
      if (pin) {
        pin.src = isActive ? '/icons/map-marker-selected.svg' : '/icons/map-marker.svg';
      }
    });
  }, []);

  // Initialize Azure Maps instance
  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    const subscriptionKey = import.meta.env.VITE_AZURE_MAPS_KEY;
    if (!subscriptionKey) {
      console.error('VITE_AZURE_MAPS_KEY is not configured');
      setMapError(true);
      return;
    }

    try {
      const map = new atlas.Map(mapContainerRef.current, {
        authOptions: {
          authType: atlas.AuthenticationType.subscriptionKey,
          subscriptionKey,
        },
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        style: 'road',
        language: 'fr-FR',
      });

      // Set ref synchronously so the guard `if (mapRef.current) return` at the
      // top of this effect correctly blocks a second invocation in React StrictMode.
      // If the ref were set inside the async `ready` callback (as before), the
      // StrictMode cleanup would find mapRef.current === null, skip dispose(), and
      // allow a second atlas.Map to be created on the same container — causing two
      // maps to race, with the second one overwriting mapRef and orphaning any
      // markers that were already added to the first map.
      mapRef.current = map;

      map.events.add('ready', () => {
        // Ensure map fills container correctly after initialization
        map.resize();

        // Restore saved viewport (e.g., returning from offer detail — AC-016.5)
        if (searchContext.mapViewport) {
          map.setCamera({
            center: searchContext.mapViewport.center,
            zoom: searchContext.mapViewport.zoom,
            type: 'jump',
          });
          // Signal the markers effect to skip auto-center so this zoom is kept
          viewportRestoredRef.current = true;
        }

        setMapReady(true);
      });

      map.events.add('error', (e: unknown) => {
        console.error('Azure Maps error:', e);
        setMapError(true);
      });

      // Save viewport on move/zoom so it can be restored after navigation (AC-016.5)
      map.events.add('moveend', () => {
        const camera = map.getCamera();
        const center = camera.center as [number, number] | undefined;
        const zoom = camera.zoom as number | undefined;
        if (center && zoom !== undefined) {
          setMapViewport({ center, zoom });
        }
      });

      // Close popup when clicking on map background — but NOT when a marker was clicked (AC-019.11)
      map.events.add('click', () => {
        if (markerClickedRef.current) {
          markerClickedRef.current = false;
          return;
        }
        setSelectedClusterIndex(null);
        setAnchorPx(null);
      });
    } catch (error) {
      console.error('Failed to initialize Azure Maps:', error);
      setMapError(true);
    }

    return () => {
      cleanupMarkers();
      if (mapRef.current) {
        mapRef.current.dispose();
        mapRef.current = null;
      }
      setMapReady(false);
    };
    // searchContext.mapViewport and setMapViewport intentionally omitted:
    // viewport restore runs once on init; ongoing saves handled by moveend listener
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add/update markers when offers change or map becomes ready
  useEffect(() => {
    if (!mapReady || !mapRef.current || isLoading) return;

    const map = mapRef.current;

    // Close any open popup before replacing markers
    setSelectedClusterIndex(null);
    setAnchorPx(null);

    // Clean existing markers
    cleanupMarkers();

    // Group co-located offers into clusters
    const clusters = clusterOffers(offers);
    clustersRef.current = clusters;

    if (clusters.length === 0) {
      offers.forEach(o => console.warn(`Offer ${o.id} skipped: missing coordinates`));
      return;
    }

    // Create one marker per cluster
    const positions: atlas.data.Position[] = [];

    clusters.forEach((cluster, idx) => {
      positions.push(cluster.position);

      const html =
        cluster.offers.length > 1
          ? createClusterMarkerHtml(cluster.offers.length, cluster.minDistance, false)
          : createMarkerHtml(cluster.offers[0].distance, false);

      const marker = new atlas.HtmlMarker({
        position: cluster.position,
        htmlContent: html,
        pixelOffset: [0, -33],
      });

      map.markers.add(marker);
      markersRef.current.push(marker);

      // Wire click via DOM listener on the marker element.
      // Using DOM addEventListener (not map.events.add) guarantees the handler runs
      // before the Azure Maps map-level 'click' event, and stopPropagation() prevents
      // the map canvas from receiving a second click that would re-close the popup.
      const markerEl = marker.getElement();
      if (markerEl) {
        markerEl.addEventListener('click', (e: MouseEvent) => {
          e.stopPropagation(); // prevent map-level click from closing popup
          markerClickedRef.current = true;

          const mapRect = mapContainerRef.current?.getBoundingClientRect();
          const outerRect = outerBoxRef.current?.getBoundingClientRect();
          const offsetX = mapRect && outerRect ? mapRect.left - outerRect.left : 0;
          const offsetY = mapRect && outerRect ? mapRect.top - outerRect.top : 0;
          const mapHeight = mapRect?.height ?? 300;
          const mapWidth = mapRect?.width ?? 400;

          // Center the marker in the canvas, preserving the current zoom.
          // The HtmlMarker has pixelOffset [0, -33], so the geo-position (pin tip)
          // lands exactly at canvas centre (mapHeight/2). The popup goes below it.
          const currentZoom = (map.getCamera().zoom as number | undefined) ?? DEFAULT_ZOOM;
          map.setCamera({
            center: cluster.position,
            zoom: currentZoom,
            type: 'ease',
            duration: 300,
          });

          // Pin tip = canvas centre; popup anchors 8px below that
          const adjustedPx = {
            x: offsetX + mapWidth / 2,
            y: offsetY + mapHeight / 2,
          };

          setSelectedClusterIndex(prev => {
            const next = prev === idx ? null : idx;
            refreshMarkerHtml(next);
            if (next === null) {
              setAnchorPx(null);
            } else {
              setAnchorPx(adjustedPx);
            }
            return next;
          });
        });
      }
    });

    // Center on the first cluster (closest offer) so the user sees it — but only
    // when loading fresh results. If the viewport was just restored from a saved
    // state (e.g., returning from offer detail), skip so the user's zoom is kept.
    if (positions.length > 0 && !viewportRestoredRef.current) {
      map.setCamera({
        center: positions[0],
        zoom: 12,
        type: 'ease',
        duration: 500,
      });
    }
    // Reset flag so the NEXT search (new offers) will auto-center again
    viewportRestoredRef.current = false;
  }, [offers, mapReady, isLoading, cleanupMarkers, refreshMarkerHtml]);

  // Update camera center when search location changes
  useEffect(() => {
    if (!mapReady || !mapRef.current) return;
    if (
      searchFilters?.latitude !== undefined &&
      searchFilters?.longitude !== undefined &&
      offers.length === 0
    ) {
      mapRef.current.setCamera({
        center: [searchFilters.longitude, searchFilters.latitude],
        zoom: 10,
        type: 'ease',
        duration: 500,
      });
    }
  }, [searchFilters?.latitude, searchFilters?.longitude, mapReady, offers.length]);

  // Error state: map unavailable
  if (mapError) {
    return (
      <Box
        data-testid="search-map-error"
        sx={{
          textAlign: 'center',
          py: 6,
          px: 2,
          backgroundColor: 'grey.50',
          borderRadius: '10px',
        }}
      >
        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
          {t('search.map.unavailable')}
        </Typography>
        {onSwitchToList && (
          <AppButton
            data-testid="search-map-back-to-list"
            variant="primary"
            onClick={onSwitchToList}
            sx={{ mt: 2 }}
          >
            {t('search.map.backToList')}
          </AppButton>
        )}
      </Box>
    );
  }

  return (
    <Box ref={outerBoxRef} data-testid="search-map-container" sx={{ position: 'relative' }}>
      {/* Loading overlay — shown on top of the map container, not replacing it */}
      {isLoading && (
        <Box
          data-testid="search-map-loading"
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            borderRadius: '10px',
          }}
        >
          <CircularProgress />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            {t('search.map.loading')}
          </Typography>
        </Box>
      )}

      {/* Map container — always rendered so Azure Maps keeps its DOM attachment */}
      {/* Help text */}
      {offers.length > 0 && mapReady && (
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mb: 1 }}
        >
          {t('search.map.clickToView')}
        </Typography>
      )}
      <Box
        id={MAP_CONTAINER_ID}
        ref={mapContainerRef}
        sx={{
          width: '100%',
          height: '60vh',
          minHeight: 300,
          borderRadius: '10px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
        }}
      />

      {/* Empty state overlay */}
      {offers.length === 0 && mapReady && !isLoading && (
        <Box
          data-testid="search-map-no-results"
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            borderRadius: '10px',
            p: 3,
            boxShadow: 2,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            {t('search.map.noResults')}
          </Typography>
        </Box>
      )}

      {/* Marker popup — anchored near the clicked marker (AC-019.1) */}
      {selectedClusterIndex !== null &&
        anchorPx !== null &&
        clustersRef.current[selectedClusterIndex] != null && (
          <MapMarkerPopup
            cluster={clustersRef.current[selectedClusterIndex]}
            anchorPx={anchorPx}
            containerRef={outerBoxRef}
            searchFilters={searchFilters}
            onClose={() => {
              refreshMarkerHtml(null);
              setSelectedClusterIndex(null);
              setAnchorPx(null);
            }}
          />
        )}
    </Box>
  );
}
